import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { MESSAGE_MAX, NAME_MAX, parseDollarsToCents, type PublicItem } from '@sellit/shared';
import { api, ApiError } from '../lib/api';
import { storage } from '../lib/format';
import { Icon } from '../lib/Icon';

const CONTACT_KEY = 'sellit.buyer';

function savedContact(): { name: string; phone: string; email: string } {
  try {
    return { name: '', phone: '', email: '', ...JSON.parse(storage.get(CONTACT_KEY) ?? '{}') };
  } catch {
    return { name: '', phone: '', email: '' };
  }
}

/** "I want this" / "Make an offer". The seller gets an email and calls or texts the buyer back. */
export function InquiryForm({ slug, item }: { slug: string; item: PublicItem }) {
  const isOffer = item.pricingMode === 'MAKE_OFFER';
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => ({ ...savedContact(), offer: '', message: '', website: '' }));
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const formRef = useRef<HTMLFormElement>(null);

  // The button sits in a bar at the bottom of the screen; bring the form into view when it opens.
  useEffect(() => {
    if (open) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [open]);

  const offerCents = form.offer.trim() === '' ? null : parseDollarsToCents(form.offer);
  const offerInvalid = form.offer.trim() !== '' && offerCents === null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy || offerInvalid) return;
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      await api.sendInquiry(slug, item.number, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        message: form.message,
        offerCents: isOffer ? offerCents : null,
        website: form.website,
      });
      storage.set(CONTACT_KEY, JSON.stringify({ name: form.name, phone: form.phone, email: form.email }));
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send. Try again.');
      if (err instanceof ApiError && err.fields) setFieldErrors(err.fields);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="sent-card" role="status">
        <span className="sent-mark"><Icon name="check" size={22} /></span>
        <div>
          <strong>Sent!</strong>
          <p>The seller has your number and will get back to you about #{item.number}.</p>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="cta-bar">
        <button className="btn primary big" onClick={() => setOpen(true)}>
          {isOffer ? 'Make an offer' : 'I want this'}
        </button>
      </div>
    );
  }

  return (
    <form className="stack inquiry-form card" onSubmit={onSubmit} ref={formRef}>
      <h2>{isOffer ? 'Make an offer' : 'I want this'}</h2>
      {item.status === 'PENDING' && <p className="notice small">Someone else is already on this one. Send anyway to be next in line.</p>}
      {isOffer && (
        <label className="field">
          <span>Your offer{item.priceCents !== null ? ` (asking ${item.priceLabel})` : ''}</span>
          <div className="money">
            <span aria-hidden="true">$</span>
            <input inputMode="decimal" placeholder="0" value={form.offer} onChange={(e) => set({ offer: e.target.value })} />
          </div>
          {offerInvalid && <small className="error">Enter an amount like 40 or 12.50</small>}
          {fieldErrors.offerCents && <small className="error">{fieldErrors.offerCents}</small>}
        </label>
      )}
      <label className="field">
        <span>Your name</span>
        <input autoComplete="name" maxLength={NAME_MAX} value={form.name} onChange={(e) => set({ name: e.target.value })} />
        {fieldErrors.name && <small className="error">{fieldErrors.name}</small>}
      </label>
      <label className="field">
        <span>Phone</span>
        <input type="tel" autoComplete="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
        {fieldErrors.phone && <small className="error">{fieldErrors.phone}</small>}
      </label>
      <label className="field">
        <span>Email (optional)</span>
        <input type="email" autoComplete="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
        {fieldErrors.email && <small className="error">{fieldErrors.email}</small>}
      </label>
      <label className="field">
        <span>Message (optional)</span>
        <textarea rows={2} maxLength={MESSAGE_MAX} placeholder="e.g. Can I pick it up Saturday morning?" value={form.message} onChange={(e) => set({ message: e.target.value })} />
      </label>
      {/* Honeypot: invisible to people, irresistible to bots. */}
      <div className="hp" aria-hidden="true">
        <label>
          Website
          <input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set({ website: e.target.value })} />
        </label>
      </div>
      <p className="muted small">
        The seller of this sale gets your name, phone and email so they can reply. We don't sell your information. By sending,
        you agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
      </p>
      {error && <p className="error" role="alert">{error}</p>}
      <button className="btn primary big" disabled={busy}>{busy ? 'Sending…' : 'Send to seller'}</button>
    </form>
  );
}
